import React, { useEffect, useMemo } from 'react'
import { Route, BrowserRouter as Router, Routes, Navigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'

import FindTutor from './pages/student/FindTutor'
import Login from './pages/common/Login/Login'
import ForgotPassword from './pages/common/ForgotPassword'
import Settings from './pages/common/settings/Settings'
import PrivacyPolicy from './pages/policies/PrivacyPolicy'
import CopyrightPolicy from './pages/policies/CopyrightPolicy'
import CookiePolicy from './pages/policies/CookiePolicy'
import CancellationAndRefundPolicy from './pages/policies/CancellationAndRefundPolicy'
import Disclaimer from './pages/policies/Disclaimer'
import CommunityGuidelines from './pages/policies/CommunityGuidelines'
import TutorTermsOfUse from './pages/policies/TutorTermsOfUse'
import TermsOfUse from './pages/policies/TermsOfUse'
import { Register } from './pages/common/Register/Register'
import ConfirmEmailRedirect from './components/pages/Register/ConfirmEmailRedirect'
import ConfirmEmail from './components/pages/Register/ConfirmEmail'
import UserLayout from './components/Layouts/UserLayout'
import DashboardLayout from './components/Layouts/DashboardLayout'
import TutorStatistic from './pages/tutor/Statistic'
import TutorMap from './pages/tutor/Map'
import StudentStatistic from './pages/student/Statistic'
import DashboardTutorLayout from './components/Layouts/DashboardTutorLayout'
import DashboardStudentLayout from './components/Layouts/DashboardStudentLayout'
import Calendar from './pages/common/Calendar'
import Trading from './pages/tutor/Trading'
import TradingSell from './pages/tutor/TradingSell'
import TradingMarket from './pages/tutor/TradingMarket'
import TradingAssets from './pages/tutor/TradingAssets'
import SettingsProfile from './pages/common/settings/Tabs/Profile'
import SettingsLanguages from './pages/common/settings/Tabs/Languages'
import SettingsLocationAndTime from './pages/common/settings/Tabs/LocationAndTime'
import SettingsSecurity from './pages/common/settings/Tabs/Security'
import SettingsNotifications from './pages/common/settings/Tabs/Notifications'
import StorageSettings from './pages/common/settings/Tabs/StorageSettings'
import SettingsAvailability from './pages/common/settings/Tabs/Availability'
import SettingsMyClassCategories from './pages/common/settings/Tabs/MyClassCategories'
import SettingsMyBanners from './pages/common/settings/Tabs/MyBanners'
import SettingsMyQuizTemplates from './pages/common/settings/Tabs/MyQuizTemplates'
import SettingsMyQuizTemplatesAdd from './pages/common/settings/Tabs/MyQuizTemplates/MyQuizTemplatesAdd'
import SettingsMyQuizTemplatesWrapper from './pages/common/settings/Tabs/MyQuizTemplates/MyQuizTemplatesWrapper'
import SettingsWithdrawing from './pages/common/settings/Tabs/Withdrawing'
import SettingsEmailNotifications from './pages/common/settings/Tabs/EmailNotifications'
import SettingsPushNotifications from './pages/common/settings/Tabs/PushNotifications'
import SettingsGoalsPreferences from './pages/common/settings/Tabs/GoalsPreferences'
import SettingsMyClassCategoriesTestProcess from './pages/common/settings/Tabs/MyClassCategories/MyClassCategoriesTestProcess'
import SettingsClassBanners from './pages/common/settings/Tabs/MyBanners/ClassBanners'
import SettingsJobsPosts from './pages/common/settings/Tabs/MyBanners/JobsPosts'
import SettingsClassBannersAdd from './pages/common/settings/Tabs/MyBanners/ClassBanners/ClassBannersAdd'
import SettingsJobsPostsAdd from './pages/common/settings/Tabs/MyBanners/JobsPosts/JobsPostsAdd'
import PasswordResetRedirect from './components/pages/Register/PasswordResetRedirect'
import ResetPassword from './pages/common/ResetPassword'
import Marketplace from './pages/Marketplace'
import Jobs from './pages/Jobs'
import Switch from './pages/common/Switch'
import ClassPage from 'pages/class'
import ClassReview from 'pages/class/ClassReview/ClassReview'
import About from 'components/pages/Register/About'
import './landingpage.css'
import { getUser, setTokenSyncComplete } from './routing/fetchingfunction/user/user.action'
import {
  selectAccessToken,
  selectTokenSyncComplete,
  selectUserData,
} from './routing/fetchingfunction/user/user.reducer'

import './App.css'
import HomePage from 'pages/HomePage'
import AboutUs from 'pages/AboutUs'
import Categories from 'pages/Categories'
import Tools from 'pages/Tools'
import useScrollReset from 'hooks/useScrollReset'

const App = () => {
  const dispatch = useDispatch()
  const accessToken = useSelector(selectAccessToken)
  const tokenSyncComplete = useSelector(selectTokenSyncComplete)

  const userData = useSelector(selectUserData)
  console.log(userData, 'User Data in App.js')

  // Check if we're on special routes that should bypass auth checks
  const isActivationRoute = window.location.pathname.startsWith('/activate/')
  const isResetPasswordRoute = window.location.pathname.includes('/reset-password')
  const isRegisterRoute = window.location.pathname.includes('/register')
  const shouldSkipAuthCheck = isActivationRoute || isResetPasswordRoute

  // ADDED: Special useEffect to clean corrupted tokens on app startup
  useEffect(() => {
    // Function to clean a potentially corrupted token
    const cleanToken = (tokenStr) => {
      if (!tokenStr || typeof tokenStr !== 'string') return tokenStr

      // Only apply cleaning if the token looks corrupted (has multiple backslashes or quotes)
      if (tokenStr.includes('\\\\') || (tokenStr.startsWith('"') && tokenStr.includes('\\"'))) {
        console.log('App: Detected potentially corrupted token, cleaning...')

        // Try recursive JSON parsing for heavily escaped tokens
        let cleanedToken = tokenStr
        let iterations = 0
        const maxIterations = 10

        try {
          while (iterations < maxIterations) {
            iterations++
            // If we've removed all quotes and escapes, we're done
            if (!cleanedToken.startsWith('"') && !cleanedToken.includes('\\')) {
              break
            }

            // Try to parse one layer of JSON if it starts with a quote
            if (cleanedToken.startsWith('"')) {
              try {
                const parsed = JSON.parse(cleanedToken)
                if (typeof parsed === 'string') {
                  cleanedToken = parsed
                } else {
                  break // Not a string result, stop parsing
                }
              } catch (e) {
                break // JSON parsing failed, stop trying
              }
            } else {
              break // No leading quote, can't be parsed as JSON
            }
          }

          // After JSON parsing, do a final cleanup with regex
          cleanedToken = cleanedToken.replace(/\\+"/g, '').replace(/^"|"$/g, '')

          // For extreme cases, try to extract the JWT pattern directly
          const jwtPattern = /eyJ[a-zA-Z0-9_-]+\.eyJ[a-zA-Z0-9_-]+\.[a-zA-Z0-9_-]+/
          const jwtMatch = cleanedToken.match(jwtPattern)
          if (jwtMatch && jwtMatch[0]) {
            cleanedToken = jwtMatch[0]
          }

          console.log('App: Token cleaned successfully')
          return cleanedToken
        } catch (e) {
          console.error('App: Error cleaning token:', e)
          return tokenStr // Return original if cleaning fails
        }
      }

      // Just strip outer quotes for simpler cases
      return tokenStr.replace(/^"|"$/g, '')
    }

    // Check and clean localStorage tokens
    const localStorageToken = localStorage.getItem('access_token')
    const localStorageRefreshToken = localStorage.getItem('refresh_token')

    // If tokens exist and look corrupted, clean them
    if (localStorageToken) {
      const cleanedToken = cleanToken(localStorageToken)
      if (cleanedToken !== localStorageToken) {
        console.log('App: Cleaned corrupted access_token in localStorage')
        localStorage.setItem('access_token', cleanedToken)
      }
    }

    if (localStorageRefreshToken) {
      const cleanedRefreshToken = cleanToken(localStorageRefreshToken)
      if (cleanedRefreshToken !== localStorageRefreshToken) {
        console.log('App: Cleaned corrupted refresh_token in localStorage')
        localStorage.setItem('refresh_token', cleanedRefreshToken)
      }
    }

    // Also check store.js
    const storeJs = require('store')
    if (storeJs && typeof storeJs.get === 'function' && typeof storeJs.set === 'function') {
      const storeToken = storeJs.get('access_token')
      const storeRefreshToken = storeJs.get('refresh_token')

      if (storeToken) {
        const cleanedStoreToken = cleanToken(storeToken)
        if (cleanedStoreToken !== storeToken) {
          console.log('App: Cleaned corrupted access_token in store.js')
          storeJs.set('access_token', cleanedStoreToken)
        }
      }

      if (storeRefreshToken) {
        const cleanedStoreRefreshToken = cleanToken(storeRefreshToken)
        if (cleanedStoreRefreshToken !== storeRefreshToken) {
          console.log('App: Cleaned corrupted refresh_token in store.js')
          storeJs.set('refresh_token', cleanedStoreRefreshToken)
        }
      }
    }
  }, []) // Run only once on app startup

  // Memoize this value to prevent unnecessary rerenders
  const pathRequiresNoAuth = useMemo(() => {
    return shouldSkipAuthCheck
  }, [shouldSkipAuthCheck])

  // Import store safely at the top level to avoid 'store is undefined' errors
  const storeJs = require('store')

  // First useEffect to handle token synchronization
  useEffect(() => {
    const localStorageToken = localStorage.getItem('access_token')
    const localStorageRefreshToken = localStorage.getItem('refresh_token')

    if (shouldSkipAuthCheck) {
      console.log(
        `App: On special route ${window.location.pathname}, skipping token sync initial check.`
      )
      dispatch(setTokenSyncComplete(true))
      return
    }

    if (accessToken) {
      console.log('App: Token already present in Redux state. Marking sync complete.')
      dispatch(setTokenSyncComplete(true))
      return
    }

    if (localStorageToken && !accessToken) {
      console.log('App: Found token in localStorage, Redux state is empty/stale. Syncing...')
      if (storeJs && typeof storeJs.set === 'function') {
        try {
          // FIXED: Ensure no quotes around the token when storing in store.js
          const cleanLocalToken =
            typeof localStorageToken === 'string' &&
            localStorageToken.startsWith('"') &&
            localStorageToken.endsWith('"')
              ? localStorageToken.substring(1, localStorageToken.length - 1)
              : localStorageToken

          storeJs.set('access_token', cleanLocalToken)

          // Initialize cleanRefreshToken to avoid undefined error
          let cleanRefreshToken = localStorageRefreshToken

          if (localStorageRefreshToken) {
            // FIXED: Ensure no quotes around the refresh token when storing in store.js
            cleanRefreshToken =
              typeof localStorageRefreshToken === 'string' &&
              localStorageRefreshToken.startsWith('"') &&
              localStorageRefreshToken.endsWith('"')
                ? localStorageRefreshToken.substring(1, localStorageRefreshToken.length - 1)
                : localStorageRefreshToken

            storeJs.set('refresh_token', cleanRefreshToken)
          }

          console.log(
            'App: store.js synced from localStorage. Dispatching to Redux and marking sync complete.'
          )
          // FIXED: Pass cleaned tokens to Redux when dispatching TOKEN_SYNC_COMPLETE
          dispatch({
            type: 'TOKEN_SYNC_COMPLETE',
            payload: {
              token: cleanLocalToken,
              refreshToken: localStorageRefreshToken ? cleanRefreshToken : undefined,
            },
          })
          dispatch(setTokenSyncComplete(true))
        } catch (error) {
          console.error('App: Error syncing tokens to store.js:', error)
          dispatch(setTokenSyncComplete(true)) // Mark complete even on error
        }
      } else {
        console.error('App: Store module not available or missing set method')
        dispatch(setTokenSyncComplete(true)) // Mark complete to avoid blocking
      }
    } else {
      console.log('App: No token in localStorage or Redux. Sync complete.')
      dispatch(setTokenSyncComplete(true))
    }
  }, [dispatch, accessToken, shouldSkipAuthCheck, storeJs])

  // Second useEffect to fetch user data only after token sync is complete
  useEffect(() => {
    if (!tokenSyncComplete) {
      console.log('App: Waiting for token sync to complete before fetching user data')
      return
    }

    const currentPath = window.location.pathname
    // Refined public path check
    const isLoginRoute = currentPath === '/login' || currentPath.startsWith('/login/')
    const isRegisterPath = currentPath.includes('/register')
    const isAccountCreation = currentPath.includes('/create-account')

    const isActivationPath = currentPath.includes('/activate')
    const isResetPasswordPath = currentPath.includes('/reset-password')
    const currentToken = accessToken || localStorage.getItem('access_token')

    // Routes that should explicitly NOT trigger getUser even if a token exists
    const noGetUserRoutes =
      isLoginRoute || isActivationPath || isResetPasswordPath || (isAccountCreation && !accessToken)

    if (currentToken && !noGetUserRoutes) {
      console.log(`App: Fetching user data on ${currentPath} with token.`)
      dispatch(getUser())
    } else if (!currentToken && !noGetUserRoutes) {
      console.log(
        `App: No token, but on a protected route ${currentPath}. App should ideally redirect to login via route guards.`
      )
      // Consider if a redirect to login should happen here if not handled by ProtectedRoute components
    } else {
      console.log(
        `App: Skipping user data fetch on ${currentPath}. Token: ${
          currentToken ? 'Exists' : 'Missing'
        }, IsNoGetUserRoute: ${noGetUserRoutes}`
      )
    }
  }, [dispatch, tokenSyncComplete, accessToken, userData]) // Removed pathRequiresNoAuth as its logic is merged

  function getCFToken() {
    const match = document.cookie.match(/CF_Authorization=([^;]+)/)
    return match ? match[1] : null
  }

  const token = getCFToken()

  useScrollReset()

  return (
    <Routes>
      {/* <Route path="/" element={<Navigate to="/login" replace />} /> */}
      <Route path="/" element={<HomePage />} />
      <Route path="/about-us" element={<AboutUs />} />
      <Route path="/tools" element={<Tools />} />
      <Route path="/categories" element={<Categories />} />

      <Route element={<UserLayout />}>
        <Route exact path="/login" element={<Login />} />
        <Route exact path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/user/activate/:uid/:token" element={<ConfirmEmail />} />
        <Route exact path="/reset-password" element={<ResetPassword />} />

        <Route exact path="/marketplace" element={<Marketplace />} />
        <Route exact path="/jobs" element={<Jobs />} />
        <Route exact path="/class" element={<ClassPage />} />
        <Route exact path="/class-review" element={<ClassReview />} />

        <Route path="/tutor">
          <Route element={<DashboardLayout />}>
            <Route element={<DashboardTutorLayout />}>
              <Route exact path="statistic" element={<TutorStatistic />} />
              <Route exact path="calendar" element={<Calendar />} />
              <Route exact path="" element={<TutorStatistic />} />
              <Route exact path="map" element={<TutorMap />} />
            </Route>
            <Route path="trading" element={<Trading />}>
              <Route exact path="" element={<TradingSell />} />
              <Route exact path="market" element={<TradingMarket />} />
              <Route exact path="assets" element={<TradingAssets />} />
            </Route>
          </Route>
          <Route path="settings" element={<Settings />}>
            <Route exact path="" element={<SettingsProfile />} />
            <Route exact path="language" element={<SettingsLanguages />} />
            <Route exact path="location" element={<SettingsLocationAndTime />} />
            <Route exact path="security" element={<SettingsSecurity />} />
            <Route path="notifications" element={<SettingsNotifications />}>
              <Route exact path="" element={<SettingsEmailNotifications />} />
              <Route exact path="push-notifications" element={<SettingsPushNotifications />} />
            </Route>
            <Route exact path="integrations" element={<StorageSettings />} />
            <Route exact path="availability" element={<SettingsAvailability />} />
            <Route exact path="goals" element={<SettingsGoalsPreferences />} />
            <Route exact path="withdrawing" element={<SettingsWithdrawing />} />
            <Route path="my-class-categories" element={<SettingsMyClassCategories />}>
              <Route exact path="test-process" element={<SettingsMyClassCategoriesTestProcess />} />
            </Route>
            <Route path="my-banners" element={<SettingsMyBanners />}>
              <Route exact path="" element={<SettingsClassBanners />} />
              <Route path="jobs-posts">
                <Route exact path="" element={<SettingsJobsPosts />} />
                <Route exact path="add" element={<SettingsJobsPostsAdd />} />
                <Route exact path="edit/:id" element={<SettingsJobsPostsAdd />} />
              </Route>
              <Route exact path="add" element={<SettingsClassBannersAdd />} />
              <Route exact path="edit/:id" element={<SettingsClassBannersAdd />} />
            </Route>
            <Route path="my-quiz-templates" element={<SettingsMyQuizTemplatesWrapper />}>
              <Route exact path="" element={<SettingsMyQuizTemplates />} />
              <Route exact path="add" element={<SettingsMyQuizTemplatesAdd />} />
              <Route exact path="edit/:id" element={<SettingsMyQuizTemplatesAdd />} />
            </Route>
          </Route>
          <Route path="register/*" element={<Register role="tutor" />} />

          <Route exact path="switch" element={<Switch role="student" />} />
        </Route>

        <Route path="/student">
          <Route element={<DashboardLayout />}>
            <Route element={<DashboardStudentLayout />}>
              <Route exact path="statistic" element={<StudentStatistic />} />
              <Route exact path="calendar" element={<Calendar />} />
              <Route exact path="" element={<StudentStatistic />} />
            </Route>
          </Route>
          <Route path="register/*" element={<Register role="student" />} />
          <Route exact path="switch" element={<Switch role="tutor" />} />
          <Route path="settings" element={<Settings />}>
            <Route exact path="" element={<SettingsProfile />} />
            <Route exact path="language" element={<SettingsLanguages />} />
            <Route exact path="location" element={<SettingsLocationAndTime />} />
            <Route exact path="security" element={<SettingsSecurity />} />
            <Route path="notifications" element={<SettingsNotifications />}>
              <Route exact path="" element={<SettingsEmailNotifications />} />
              <Route exact path="push-notifications" element={<SettingsPushNotifications />} />
            </Route>
            <Route exact path="integrations" element={<StorageSettings />} />
            <Route exact path="goals" element={<SettingsGoalsPreferences />} />
            <Route exact path="withdrawing" element={<SettingsWithdrawing />} />
          </Route>
          <Route exact path="find-tutor" element={<FindTutor />} />
        </Route>
        <Route exact path="/findtutor" element={<FindTutor />} />
      </Route>

      <Route exact path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route exact path="/copyright-policy" element={<CopyrightPolicy />} />
      <Route exact path="/cookie-policy" element={<CookiePolicy />} />
      <Route
        exact
        path="/cancellation-and-refund-policy"
        element={<CancellationAndRefundPolicy />}
      />
      <Route exact path="/disclaimer" element={<Disclaimer />} />
      <Route exact path="/community-guidelines" element={<CommunityGuidelines />} />
      <Route exact path="/tutor-terms-of-use" element={<TutorTermsOfUse />} />
      <Route exact path="/terms-of-use" element={<TermsOfUse />} />

      <Route exact path="/activate/:uid/:token" element={<ConfirmEmailRedirect />} />
      {/* Keep the original route for backward compatibility */}
      <Route exact path="/activate/" element={<ConfirmEmailRedirect />} />

      <Route
        exact
        path="/user/password-reset/:token_type/:token"
        element={<PasswordResetRedirect />}
      />
      <Route exact path="/about/" element={<ConfirmEmail />} />
    </Routes>
  )
}

export default App
